/**
 * Per-role system access derived from the role × system permission matrix.
 *
 * Used by the shell to filter the system menu, gate the Hub, and protect
 * routes the user shouldn't reach. This is UX-level only — the API is the
 * authoritative gate (server-side scope check on every request).
 */
import { PERMS } from '../data/mocks/platform';
import type { Suite } from '../data/types/platform';
import type { Claims } from './claims';

/** All system ids a role can `view` or above. Returns `null` (= unrestricted)
 *  for roles not present in the matrix, so an unknown role doesn't silently
 *  hide all systems. */
export function allowedSystemsForRole(role: string): string[] | null {
  const row = (PERMS as Record<string, Record<string, string | null>>)[role];
  if (!row) return null;
  return Object.entries(row)
    .filter(([, level]) => level === 'view' || level === 'edit' || level === 'admin')
    .map(([sysId]) => sysId);
}

/** Filter the suites list by the active user's role. Returns the original
 *  list when the role is not constrained. */
export function filterSuitesForClaims(suites: Suite[], claims: Claims | null): Suite[] {
  if (!claims) return suites;
  const allowed = allowedSystemsForRole(claims.role);
  if (allowed === null) return suites;
  const allowedSet = new Set(allowed);
  return suites.filter((s) => allowedSet.has(s.id));
}

/** True when the user can reach the /admin tree. */
export function canAccessAdmin(claims: Claims | null): boolean {
  return claims?.role === 'platform-admin';
}
